/* eslint-disable no-undef */


import React, { useEffect, useState } from "react";
import axios from "axios";
import fetchData from "../../functions/fetchData";
import updateData from "../../functions/updateData";
import Layout from "../../layout/Layout";
import ExportButtons from "../../components/ExportButtons";
import { getHeaders } from "../../utility/getHeader";



const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    contact: "",
    address: "",
    opening_balance: 0
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      await fetchData("supplier", setSuppliers, "id", {});
    } catch (error) {
      setError("Failed to fetch suppliers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: name === 'opening_balance' ? Number(value) : value 
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.contact.trim()) {
      setError("Please enter supplier name and contact");
      return;
    }
    try {
      setLoading(true);
      setError("");
      
      // Use the new supplier creation API endpoint
      const response = await axios.post("/suppliers", formData, getHeaders());
      
      if (response.data && response.data.success) {
        setSuccess("Supplier added successfully!");
        setFormData({ code: "", name: "", contact: "", address: "", opening_balance: 0 });
        fetchSuppliers();
      } else {
        setError(response.data?.message || "Error adding supplier");
      }
    } catch (err) {
      console.error('Error adding supplier:', err);
      const errorMessage = err.response?.data?.message || err.message || "Error adding supplier";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (supplier) => {
    setEditingItem(supplier);
    setEditFormData({
      code: supplier.code,
      name: supplier.name,
      contact: supplier.contact,
      address: supplier.address
    });
    setShowEditModal(true);
  };

  // Handle update
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim() || !editFormData.contact.trim()) {
      setError("Please enter supplier name and contact");
      return;
    }
    try {
      setLoading(true);
      setError("");
      
      // Use the new supplier update API endpoint
      const response = await axios.put(`/suppliers/${editingItem.id}`, editFormData, getHeaders());
      
      if (response.data && response.data.success) {
        setSuccess("Supplier updated successfully!");
        setShowEditModal(false);
        setEditingItem(null);
        setEditFormData({});
        fetchSuppliers();
      } else {
        setError(response.data?.message || "Error updating supplier");
      }
    } catch (err) {
      console.error('Error updating supplier:', err);
      const errorMessage = err.response?.data?.message || err.message || "Error updating supplier";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (supplier) => {
    if (window.confirm(`Are you sure you want to delete "${supplier.name}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        setError("");
        const whereCondition = { id: supplier.id };
        await updateData("supplier", {}, whereCondition, "DELETE");
        setSuccess("Supplier deleted successfully!");
        fetchSuppliers();
      } catch (err) {
        setError("Error deleting supplier");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Close modal
  const closeModal = () => {
    setShowEditModal(false);
    setEditingItem(null);
    setEditFormData({});
  };

  // Filter suppliers based on search term
  const filteredSuppliers = suppliers.filter((supplier) =>
    supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contact?.toString().includes(searchTerm) ||
    supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.taxid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.id?.toString().includes(searchTerm)
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSuppliers = filteredSuppliers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Calculate stats
  const totalSuppliers = suppliers.length;

  return (
    <Layout>
      <div className="ledger-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Supplier Management</h4>
        </div>

        {error && (
          <div className="alert alert-danger alert-custom">
            <i className="zmdi zmdi-close-circle mr-2"></i>
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-custom">
            <i className="zmdi zmdi-check-circle mr-2"></i>
            {success}
          </div>
        )}

        {/* <div className="row mb-4">
          <div className="col-md-6">
            <div className="ledger-stats bg-primary text-white">
              <h4>{totalSuppliers}</h4>
              <p>Total Suppliers</p>
            </div>
          </div>
        </div> */}

        <div className="row">
          {/* Form Section */}
          <div className="col-md-4">
            <div className="form-section">
              <h6><i className="zmdi zmdi-plus-circle mr-2"></i> Add New Supplier</h6>
              <form onSubmit={handleSubmit} className="ledger-form">
                <div className="form-group">
                  <label className="control-label">Supplier Code *</label>
                  <input
                    type="text"
                    name="code"
                    className="form-control"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="Enter supplier code"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="control-label">Supplier Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter supplier name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="control-label">Contact</label>
                  <input
                    type="text"
                    name="contact"
                    className="form-control"
                    value={formData.contact}
                    onChange={handleInputChange}
                    placeholder="Enter contact number"
                  />
                </div>
                <div className="form-group">
                  <label className="control-label">Address</label>
                  <textarea
                    name="address"
                    className="form-control"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                    rows="3"
                  ></textarea>
                </div>
                <div className="form-group">
                  <label className="control-label">Opening Balance</label>
                  <input
                    type="number"
                    name="opening_balance"
                    className="form-control"
                    value={formData.opening_balance}
                    onChange={handleInputChange}
                    placeholder="Enter opening balance"
                    min="0"
                    step="0.01"
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-submit btn-block"
                  disabled={loading}
                >
                  {loading && <span className="loading-spinner"></span>}
                  Add Supplier
                </button>
              </form>
            </div>
          </div>

          {/* Table Section */}
          <div className="col-md-8">
            <div className="table-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6><i className="zmdi zmdi-view-list mr-2"></i> Existing Suppliers ({filteredSuppliers.length})</h6>
              </div>
              <div className="ledger-table-panel">
                <table className="table table-striped ledger-table">
                  <thead>
                    <tr>
                      <td colSpan={2}>
                        <div className="search-box">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search suppliers..."
                            value={searchTerm}
                            onChange={handleSearch}
                          />
                        </div>
                      </td>
                      <td colSpan={5} style={{ textAlign: 'right' }}>
                        <ExportButtons
                          data={filteredSuppliers}
                          filename="suppliers"
                          title="Suppliers Report"
                          columns={[
                            { key: 'id', label: 'ID' },
                            { key: 'name', label: 'Name' },
                            { key: 'company_name', label: 'Company Name' },
                            { key: 'contact', label: 'Contact' },
                            { key: 'email', label: 'Email' },
                            { key: 'taxid', label: 'Tax ID' },
                            { key: 'address', label: 'Address' }
                          ]}
                          size="sm"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Company Name</th>
                      <th>Contact</th>
                      <th>Email</th>
                      <th>Tax ID</th>
                      <th>Address</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSuppliers.map((supplier) => (
                      <tr key={supplier.id}>
                        <td>{supplier.id}</td>
                        <td>{supplier.name}</td>
                        <td>{supplier.company_name}</td>
                        <td>{supplier.contact}</td>
                        <td>{supplier.email}</td>
                        <td>{supplier.taxid}</td>
                        <td>{supplier.address}</td>
                        <td className="action-buttons">
                          <button
                            className="btn btn-info btn-xs"
                            title="Edit"
                            onClick={() => handleEdit(supplier)}
                          >
                            <i className="zmdi zmdi-edit"></i>
                          </button>
                          <button
                            className="btn btn-danger btn-xs ml-1"
                            title="Delete"
                            onClick={() => handleDelete(supplier)}
                          >
                            <i className="zmdi zmdi-delete"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-wrapper mt-3">
                  <nav>
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        return (
                          <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                  <div className="pagination-info text-center mt-2">
                    <small className="text-muted">
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredSuppliers.length)} of {filteredSuppliers.length} entries
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal" style={{ display: 'block' }}>
            <div className="modal-backdrop" onClick={closeModal}></div>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h4>Edit Supplier</h4>
                  <button type="button" className="close" onClick={closeModal}>
                    <span>&times;</span>
                  </button>
                </div>
                <form onSubmit={handleUpdate}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label className="control-label">Supplier Code *</label>
                      <input
                        type="text"
                        name="code"
                        className="form-control"
                        value={editFormData.code || ''}
                        onChange={handleEditInputChange}
                        placeholder="Enter supplier code"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="control-label">Supplier Name *</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={editFormData.name || ''}
                        onChange={handleEditInputChange}
                        placeholder="Enter supplier name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="control-label">Contact</label>
                      <input
                        type="text"
                        name="contact"
                        className="form-control"
                        value={editFormData.contact || ''}
                        onChange={handleEditInputChange}
                        placeholder="Enter contact number"
                      />
                    </div>
                    <div className="form-group">
                      <label className="control-label">Address</label>
                      <textarea
                        name="address"
                        className="form-control"
                        value={editFormData.address || ''}
                        onChange={handleEditInputChange}
                        placeholder="Enter address"
                        rows="3"
                      ></textarea>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-default" onClick={closeModal}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading && <span className="loading-spinner"></span>}
                      Update Supplier
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Suppliers;
